import { useState } from "react";
import { Sparkles } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface PriceModelInputReasoningProps {
  /** The agent's rationale for the field's value. */
  reasoning: string;
  /** Names the trigger for screen readers. */
  ariaLabel: string;
}

/**
 * Sparkle affordance revealing the agent's reasoning for a price model input
 * value. Opens on hover or focus and pins open on click so the reasoning is
 * reachable and dismissible by keyboard alone.
 * @param props - The reasoning text and the trigger's screen reader name
 * @returns The sparkle trigger and its reasoning popover
 */
export function PriceModelInputReasoning({
  reasoning,
  ariaLabel,
}: PriceModelInputReasoningProps) {
  const [pinned, setPinned] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null);

  const dismiss = () => {
    setPinned(false);
    setPreviewing(false);
  };

  return (
    <Popover
      open={pinned || previewing}
      onOpenChange={(open) => {
        if (!open) {
          dismiss();
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          ref={setTrigger}
          type="button"
          aria-label={ariaLabel}
          className="shrink-0 cursor-pointer rounded-sm text-muted-foreground/70 transition-colors hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          onClick={(event) => {
            // The popover's own toggle would close a hover-opened popover.
            event.preventDefault();
            setPinned((current) => !current);
          }}
          onMouseEnter={() => setPreviewing(true)}
          onMouseLeave={() => setPreviewing(false)}
          onFocus={() => setPreviewing(true)}
          onBlur={() => setPreviewing(false)}
        >
          <Sparkles className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        // Inside a modal sheet, a popover portaled to the body loses wheel
        // scrolling to the sheet's scroll lock.
        container={
          trigger?.closest<HTMLElement>('[role="dialog"]') ?? undefined
        }
        className="w-72 p-3"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <p className="whitespace-pre-line text-xs italic text-muted-foreground">
          {reasoning}
        </p>
      </PopoverContent>
    </Popover>
  );
}
